import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Circle, Polygon, useMap } from 'react-leaflet';
import { 
  RefreshCw, Truck, Navigation, Clock, 
  Zap, MapPin, TrendingUp, Users,
  Play, Pause, RotateCcw, SkipBack, SkipForward, X, Map,
  Filter, AlertTriangle, Eye, Target, Search, BarChart3, Shield, Home, Activity
} from 'lucide-react';
import LoadingSpinner from '../ui/LoadingSpinner';
import SafetyDashboard from '../SafetyDashboard';
import AnalyticsDashboard from '../AnalyticsDashboard';
import GeofenceMonitor from '../GeofenceMonitor';
import EventsTimeline from '../EventsTimeline';
import VehicleSearch from '../VehicleSearch';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { DivIcon } from 'leaflet';

// Fix for Leaflet default icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface IturanVehicle {
  vehicle_id: string;
  plate: string;
  name: string;
  position: {
    lat: number;
    lng: number;
  };
  speed: number;
  heading: string;
  address: string;
  status: string;
  connection_status: 'online' | 'idle' | 'offline';
  mileage: number;
  driver: string;
  last_update: string;
  seconds_since_update: number;
  vehicle_type?: 'truck' | 'van' | 'car' | 'bus';
  ignition?: boolean;
  has_alert?: boolean;
  events?: Array<{
    event_id: string;
    event_type: string;
    event_description: string;
    timestamp: string;
    severity: string;
  }>;
  dashcam?: DashcamMapping;
}

interface DashcamMapping {
  vehicle_plate: string;
  mettax_device_id: string;
  device_name: string;
  channel_count: number;
  default_channel: number;
  is_active: boolean;
}

interface Geofence {
  id: number;
  name: string;
  description: string;
  geometry: {
    type: string;
    coordinates: number[] | number[][];
  };
  type: 'circle' | 'polygon';
  radius?: number;
  color: string;
  is_active: boolean;
}

// interface Statistics {
//   total_vehicles: number;
//   by_status: {
//     online?: number;
//     idle?: number;
//     offline?: number;
//   };
//   vehicles_moving: number;
//   avg_speed_moving: number;
//   total_distance_today: number;
//   tracking_points_24h: number;
// }

// API Configuration
const API_BASE_URL = window.location.hostname === 'localhost' 
  ? 'http://fleet.host/api'
  : '/api';

// Map tile configurations (no API key required)
const MAP_TILES = {
  osm: {
    name: 'OpenStreetMap',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
  },
  satellite: {
    name: 'Satellite',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
  },
  topo: {
    name: 'Topographic',
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: 'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)'
  },
  terrain: {
    name: 'Terrain',
    url: 'https://stamen-tiles-{s}.a.ssl.fastly.net/terrain/{z}/{x}/{y}{r}.png',
    attribution: 'Map tiles by <a href="http://stamen.com">Stamen Design</a>, <a href="http://creativecommons.org/licenses/by/3.0">CC BY 3.0</a> &mdash; Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
  },
  dark: {
    name: 'Dark',
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
  }
};



// Create custom vehicle icon with rotation, status, vehicle type, and ignition awareness
const createIturanVehicleIcon = (
  _connectionStatus: string, 
  speed: number,
  vehicleType: string = 'truck',
  ignitionOn: boolean = false,
  isSelected: boolean = false,
  hasAlert: boolean = false
) => {
  // Dynamic color logic based on state - alerts only affect the small indicator, not the main icon
  let color = '#6B7280'; // Default gray
  
  if (ignitionOn && speed > 5) {
    // Primary blue when engine on and moving
    color = '#40ABD9';
  } else if (ignitionOn && speed <= 5) {
    // Orange when engine on but stationary (idling)
    color = '#F59E0B';
  } else if (!ignitionOn) {
    // Gray when engine off
    color = '#6B7280';
  }
  
  const size = isSelected ? 48 : 40;
  const isMoving = speed > 5;
  
  // Vehicle type icons (SVG paths)
  const vehicleIcons = {
    truck: `<path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"></path>
            <path d="M15 18H9"></path>
            <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"></path>
            <circle cx="17" cy="18" r="2"></circle>
            <circle cx="7" cy="18" r="2"></circle>`,
    van: `<path d="M5 11V5h11l3 3v3"></path>
          <path d="M5.5 16a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z"></path>
          <path d="M18.5 16a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z"></path>
          <path d="M5 11h14v5H5z"></path>`,
    car: `<path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"></path>
          <circle cx="7" cy="17" r="2"></circle>
          <circle cx="17" cy="17" r="2"></circle>`,
    bus: `<path d="M8 6v6"></path>
          <path d="M15 6v6"></path>
          <path d="M2 12h19.6"></path>
          <path d="M18 16h3a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h3"></path>
          <circle cx="7" cy="16" r="2"></circle>
          <circle cx="17" cy="16" r="2"></circle>`
  };
  
  const icon = vehicleIcons[vehicleType as keyof typeof vehicleIcons] || vehicleIcons.truck;
  
  return new DivIcon({
    html: `
      <div style="position: relative; width: ${size}px; height: ${size}px;">
        ${isMoving ? `
          <div style="
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: ${size + 20}px;
            height: ${size + 20}px;
            border: 2px solid ${color};
            border-radius: 50%;
            opacity: 0.3;
            animation: pulse 1.5s infinite;
          "></div>
        ` : ''}
        <div style="
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: ${size}px;
          height: ${size}px;
          background: ${color};
          border: 3px solid white;
          border-radius: 50%;
          box-shadow: 0 4px 6px rgba(0,0,0,0.2), 0 2px 4px rgba(0,0,0,0.1);
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <svg width="${size * 0.5}" height="${size * 0.5}" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            ${icon}
          </svg>
        </div>
        ${hasAlert ? `
          <div style="
            position: absolute;
            top: -2px;
            right: -2px;
            width: 14px;
            height: 14px;
            background: #EF4444;
            border: 2px solid white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            animation: alertPulse 1s infinite;
          ">
            <svg width="8" height="8" viewBox="0 0 24 24" fill="white">
              <path d="M12 9v4m0 4h.01M3.172 18.828A4 4 0 0 1 2 16.243V7.757a2 2 0 0 1 .586-1.414l5.172-5.172a2 2 0 0 1 1.414-.586h8.486a2 2 0 0 1 1.414.586l5.172 5.172a2 2 0 0 1 .586 1.414v8.486a4 4 0 0 1-1.172 2.828l-5.172 5.172a2 2 0 0 1-1.414.586H7.757a2 2 0 0 1-1.414-.586z"/>
            </svg>
          </div>
        ` : ignitionOn ? `
          <div style="
            position: absolute;
            top: -2px;
            right: -2px;
            width: 12px;
            height: 12px;
            background: ${speed > 5 ? '#10B981' : '#F59E0B'};
            border: 2px solid white;
            border-radius: 50%;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          "></div>
        ` : ''}
        ${isSelected ? `
          <div style="
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: ${size + 16}px;
            height: ${size + 16}px;
            border: 3px solid ${color};
            border-radius: 50%;
            opacity: 0.5;
          "></div>
        ` : ''}
      </div>
      <style>
        @keyframes pulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.3; }
          50% { transform: translate(-50%, -50%) scale(1.1); opacity: 0.1; }
        }
        @keyframes alertPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.2); }
        }
      </style>
    `,
    className: 'custom-ituran-marker',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

// Create direction-aware arrow icon for trip replay
const createReplayArrowIcon = (
  heading: number,
  speed: number,
  hasEvent: boolean = false
) => {
  // Color based on event or speed
  let color = '#40ABD9'; // Default blue (moving)
  
  if (hasEvent) {
    color = '#EF4444'; // Red for events (hard braking, speeding, etc)
  } else if (speed > 80) {
    color = '#F59E0B'; // Orange for high speed
  } else if (speed < 5) {
    color = '#6B7280'; // Gray for stopped/slow
  }
  
  const size = 36;
  
  return new DivIcon({
    html: `
      <div style="
        position: relative;
        width: ${size}px;
        height: ${size}px;
        transform: rotate(${heading}deg);
      ">
        <div style="
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
        ">
          <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none">
            <!-- Arrow shape pointing up (will rotate based on heading) -->
            <path d="M12 2 L20 20 L12 16 L4 20 Z" 
                  fill="${color}" 
                  stroke="white" 
                  stroke-width="1.5" 
                  stroke-linejoin="round"/>
            ${hasEvent ? `
              <!-- Event indicator circle -->
              <circle cx="12" cy="12" r="4" fill="white" opacity="0.9"/>
              <text x="12" y="14" 
                    text-anchor="middle" 
                    font-size="8" 
                    font-weight="bold" 
                    fill="${color}">!</text>
            ` : ''}
          </svg>
        </div>
        ${speed > 5 ? `
          <!-- Motion pulse for moving vehicles -->
          <div style="
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: ${size + 10}px;
            height: ${size + 10}px;
            border: 2px solid ${color};
            border-radius: 50%;
            opacity: 0.4;
            animation: arrowPulse 1.5s infinite;
          "></div>
        ` : ''}
      </div>
      <style>
        @keyframes arrowPulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.4; }
          50% { transform: translate(-50%, -50%) scale(1.3); opacity: 0.1; }
        }
      </style>
    `,
    className: 'custom-replay-arrow',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

// Component to fit map bounds to all vehicles
const FitBoundsToVehicles: React.FC<{ vehicles: IturanVehicle[]; trigger?: number }> = ({ vehicles, trigger }) => {
  const map = useMap();
  const [hasInitialized, setHasInitialized] = useState(false);
  const [lastTrigger, setLastTrigger] = useState(0);
  
  useEffect(() => {
    // Only fit bounds on initial load (when vehicles first load)
    if (vehicles.length > 0 && !hasInitialized) {
      const bounds = L.latLngBounds(
        vehicles.map(v => [v.position.lat, v.position.lng] as [number, number])
      );
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
      setHasInitialized(true);
    }
  }, [vehicles, map, hasInitialized]);
  
  useEffect(() => {
    // Only fit bounds when trigger changes (manual reset)
    if (trigger && trigger !== lastTrigger && vehicles.length > 0) {
      const bounds = L.latLngBounds(
        vehicles.map(v => [v.position.lat, v.position.lng] as [number, number])
      );
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
      setLastTrigger(trigger);
    }
  }, [trigger, lastTrigger, vehicles, map]);
  
  return null;
};

// Component to pan/zoom to selected vehicle
const PanToSelectedVehicle: React.FC<{ vehicle: IturanVehicle | null; autoFollow: boolean }> = ({ vehicle, autoFollow }) => {
  const map = useMap();
  
  // Pan once when vehicle is first selected
  useEffect(() => {
    if (vehicle) {
      map.setView([vehicle.position.lat, vehicle.position.lng], 16, { animate: true });
    }
  }, [vehicle?.vehicle_id, map]);
  
  // Continuously follow vehicle position when autoFollow is enabled
  useEffect(() => {
    if (vehicle && autoFollow) {
      map.setView([vehicle.position.lat, vehicle.position.lng], 16, { animate: true });
    }
  }, [vehicle?.position.lat, vehicle?.position.lng, autoFollow, map]);
  
  return null;
};

// Component to keep replay marker in view (only pan when out of bounds)
const KeepMarkerInView: React.FC<{ lat: number; lng: number }> = ({ lat, lng }) => {
  const map = useMap();
  
  useEffect(() => {
    const bounds = map.getBounds();
    const point = L.latLng(lat, lng);
    
    // Only pan if the point is outside the current view
    if (!bounds.contains(point)) {
      map.panTo(point, { animate: true, duration: 0.5 });
    }
  }, [lat, lng, map]);
  
  return null;
};

const IturanLiveMap: React.FC = () => {
  const [vehicles, setVehicles] = useState<IturanVehicle[]>([]);
  // const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<IturanVehicle | null>(null);
  const [showOnlineOnly, setShowOnlineOnly] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(10); // seconds
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [mapCenter] = useState<[number, number]>([-26.2041, 28.0473]); // Default to Johannesburg
  const [sidebarOpen, setSidebarOpen] = useState(true); // Mobile sidebar toggle
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Map layer states
  const [currentTile, setCurrentTile] = useState<keyof typeof MAP_TILES>('osm');
  const [showMapSwitcher, setShowMapSwitcher] = useState(false);

  // Calculate filtered vehicles
  const filteredVehicles = useMemo(() => {
    return vehicles.filter((vehicle) => {
      // Search filter
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery || 
        vehicle.plate.toLowerCase().includes(searchLower) ||
        (vehicle.driver && vehicle.driver.toLowerCase().includes(searchLower));

      // Status filter
      const vehicleStatus = vehicle.has_alert ? 'alert' :
        vehicle.ignition && vehicle.speed > 5 ? 'moving' :
        vehicle.ignition && vehicle.speed <= 5 ? 'idling' : 'stopped';
      const matchesStatus = statusFilter === 'all' || vehicleStatus === statusFilter;

      // Type filter
      const matchesType = typeFilter === 'all' || vehicle.vehicle_type === typeFilter;

      return matchesSearch && matchesStatus && matchesType;
    });
  }, [vehicles, searchQuery, statusFilter, typeFilter]);
  
  // Overlay expanded state
  const [isOverlayExpanded, setIsOverlayExpanded] = useState(false);
  
  // Modal states for navigation
  const [showSafetyModal, setShowSafetyModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [showGeofenceModal, setShowGeofenceModal] = useState(false);
  const [showEventsModal, setShowEventsModal] = useState(false);
  const [showVehicleSearch, setShowVehicleSearch] = useState(false);
  
  // Map reset trigger
  const [resetMapTrigger, setResetMapTrigger] = useState(0);
  
  // Trip replay states
  const [isReplayMode, setIsReplayMode] = useState(false);
  const [replayVehicle, setReplayVehicle] = useState<IturanVehicle | null>(null);
  const [tripData, setTripData] = useState<any>(null);
  const [detectedTrips, setDetectedTrips] = useState<any[]>([]);
  const [selectedTrip, setSelectedTrip] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(5);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [isLoadingTrip, setIsLoadingTrip] = useState(false);
  const intervalRef = React.useRef<number | null>(null);
  
  // Geofence states
  const [geofences, setGeofences] = useState<Geofence[]>([]);
  const [showGeofences, setShowGeofences] = useState(false);
  
  // Auto-follow state
  const [autoFollow, setAutoFollow] = useState(false);
  
  // Dashcam states
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoModalData, setVideoModalData] = useState<{
    vehicle: IturanVehicle;
    type: 'live' | 'historical';
    url?: string;
    startTime?: string;
    endTime?: string;
    currentChannel?: number;
  } | null>(null);
  const [dashcamMappings, setDashcamMappings] = useState<{[plate: string]: DashcamMapping}>({});
  const [isLoadingLiveVideo, setIsLoadingLiveVideo] = useState(false);
  const [isLoadingHistoricalVideo, setIsLoadingHistoricalVideo] = useState(false);

  // Command modal states
  const [showCommandModal, setShowCommandModal] = useState(false);
  const [commandVehicle, setCommandVehicle] = useState<IturanVehicle | null>(null);
  const [isLoadingCommand, setIsLoadingCommand] = useState(false);

  // Fetch live tracking data
  const fetchLiveTracking = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (showOnlineOnly) {
        params.append('online_only', 'true');
      }

      const response = await fetch(`${API_BASE_URL}/ituran-live-tracking.php?${params}`);
      const data = await response.json();

      if (data.success) {
        // Transform API data to match component interface
        const transformedVehicles = (data.data || []).map((v: any) => {
          // Infer vehicle type from plate pattern or default to truck
          // You can enhance this logic based on your fleet composition
          let vehicle_type: 'truck' | 'van' | 'car' | 'bus' = 'truck';
          const plate = v.plate || '';
          if (plate.includes('VAN') || plate.startsWith('KH')) {
            vehicle_type = 'van';
          } else if (plate.includes('BUS')) {
            vehicle_type = 'bus';
          } else if (plate.includes('CAR')) {
            vehicle_type = 'car';
          }
          
          // Use server-provided alert status and events
          const hasAlert = v.has_alert === true || v.has_alert === 'true';
          const events = v.events || [];
          
          return {
            vehicle_id: v.vehicle_id || v.plate,
            plate: v.plate,
            name: v.name || v.plate,
            position: {
              lat: parseFloat(v.latitude || v.position?.lat || 0),
              lng: parseFloat(v.longitude || v.position?.lng || 0)
            },
            speed: parseFloat(v.speed || 0),
            heading: v.heading || v.Head || '',
            address: v.address || '',
            status: v.status || 'unknown',
            connection_status: v.connection_status || 'offline',
            mileage: parseFloat(v.mileage || 0),
            driver: v.driver_name || v.driver || '',
            last_update: v.last_update || '',
            seconds_since_update: parseInt(v.seconds_since_update || 0),
            ignition: v.ignition_on === true || v.ignition_on === 'true',
            vehicle_type,
            has_alert: hasAlert,
            events: events
          };
        });
        
        setVehicles(transformedVehicles);
        setLastUpdate(new Date());
        setError(null);
      } else {
        throw new Error(data.error || 'Failed to fetch tracking data');
      }
    } catch (err) {
      console.error('Failed to fetch live tracking:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch tracking data');
    }
  }, [showOnlineOnly]);

  // Fetch statistics
  const fetchStatistics = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/ituran-statistics.php`);
      const data = await response.json();

      if (data.success) {
        // setStatistics(data.statistics);
      }
    } catch (err) {
      console.error('Failed to fetch statistics:', err);
    }
  }, []);

  // Fetch geofences
  const fetchGeofences = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/ituran-geofences.php`);
      const data = await response.json();

      if (data.success) {
        console.log(`Loaded ${data.count || 0} geofences from Ituran API`);
        setGeofences(data.data || []);
      } else {
        console.error('Failed to fetch geofences:', data.error);
      }
    } catch (err) {
      console.error('Failed to fetch geofences:', err);
    }
  }, []);

  // Fetch dashcam mappings
  const fetchDashcamMappings = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/vehicle-dashcam-mapping.php`);
      const data = await response.json();

      if (data.success && data.data) {
        const mappingsObj: {[plate: string]: DashcamMapping} = {};
        data.data.forEach((mapping: DashcamMapping) => {
          mappingsObj[mapping.vehicle_plate] = mapping;
        });
        setDashcamMappings(mappingsObj);
      }
    } catch (err) {
      console.error('Failed to fetch dashcam mappings:', err);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchLiveTracking(), fetchStatistics(), fetchGeofences(), fetchDashcamMappings()]);
      setLoading(false);
    };

    loadData();
  }, [fetchLiveTracking, fetchStatistics, fetchGeofences, fetchDashcamMappings]);

  // Auto-refresh setup
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchLiveTracking();
      fetchStatistics();
    }, refreshInterval * 1000);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, fetchLiveTracking, fetchStatistics]);

  // Update selected vehicle with fresh data when vehicles refresh
  useEffect(() => {
    if (selectedVehicle && vehicles.length > 0) {
      const updatedVehicle = vehicles.find(v => v.vehicle_id === selectedVehicle.vehicle_id);
      if (updatedVehicle) {
        setSelectedVehicle(updatedVehicle);
      }
    }
  }, [vehicles]);

  // Close map switcher when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showMapSwitcher) {
        const target = event.target as Element;
        if (!target.closest('.map-switcher-container')) {
          setShowMapSwitcher(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMapSwitcher]);

  // Manual refresh handler
  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([fetchLiveTracking(), fetchStatistics()]);
    setLoading(false);
  };

  // Fetch trip data for replay
  // Detect individual trips from GPS points (DEPRECATED: now using API trips)
  /* const detectTrips = (points: any[]) => {
    if (!points || points.length === 0) return [];

    const trips: any[] = [];
    let currentTrip: any = null;
    const STOP_THRESHOLD_MINUTES = 5; // Consider stopped if stationary for 5+ minutes
    const STOP_SPEED_THRESHOLD = 3; // km/h

    for (let i = 0; i < points.length; i++) {
      const point = points[i];
      const speed = parseFloat(point.speed || 0);
      const isMoving = speed > STOP_SPEED_THRESHOLD;

      if (isMoving) {
        if (!currentTrip) {
          // Start a new trip
          currentTrip = {
            points: [point],
            start_time: point.timestamp,
            start_location: point.address,
            total_distance: 0,
            max_speed: speed,
            speeds: [speed],
          };
        } else {
          // Continue current trip
          currentTrip.points.push(point);
          currentTrip.speeds.push(speed);
          currentTrip.max_speed = Math.max(currentTrip.max_speed, speed);
          
          // Calculate distance between last two points
          if (currentTrip.points.length >= 2) {
            const prev = currentTrip.points[currentTrip.points.length - 2];
            const curr = point;
            const distance = calculateDistance(
              prev.latitude, prev.longitude,
              curr.latitude, curr.longitude
            );
            currentTrip.total_distance += distance;
          }
        }
      } else {
        // Vehicle stopped
        if (currentTrip && currentTrip.points.length > 0) {
          // Check if this is end of trip (stopped for sufficient time)
          const lastPoint = currentTrip.points[currentTrip.points.length - 1];
          const timeDiff = (new Date(point.timestamp).getTime() - new Date(lastPoint.timestamp).getTime()) / 1000 / 60;
          
          if (timeDiff >= STOP_THRESHOLD_MINUTES || i === points.length - 1) {
            // End the trip
            currentTrip.end_time = lastPoint.timestamp;
            currentTrip.end_location = lastPoint.address;
            currentTrip.duration = (new Date(currentTrip.end_time).getTime() - new Date(currentTrip.start_time).getTime()) / 1000;
            currentTrip.avg_speed = currentTrip.speeds.reduce((a: number, b: number) => a + b, 0) / currentTrip.speeds.length;
            
            // Only add trips with meaningful distance (> 0.1 km)
            if (currentTrip.total_distance > 0.1 && currentTrip.points.length > 5) {
              trips.push(currentTrip);
            }
            currentTrip = null;
          }
        }
      }
    }

    // Add final trip if still in progress
    if (currentTrip && currentTrip.points.length > 5) {
      const lastPoint = currentTrip.points[currentTrip.points.length - 1];
      currentTrip.end_time = lastPoint.timestamp;
      currentTrip.end_location = lastPoint.address;
      currentTrip.duration = (new Date(currentTrip.end_time).getTime() - new Date(currentTrip.start_time).getTime()) / 1000;
      currentTrip.avg_speed = currentTrip.speeds.reduce((a: number, b: number) => a + b, 0) / currentTrip.speeds.length;
      
      if (currentTrip.total_distance > 0.1) {
        trips.push(currentTrip);
      }
    }

    return trips;
  }; */

  // Calculate distance between two GPS coordinates (Haversine formula) - DEPRECATED
  /* const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }; */

  const fetchTripData = async (vehicle: IturanVehicle, date: string) => {
    setIsLoadingTrip(true);
    try {
      // Fetch trips metadata only (no points)
      const response = await fetch(
        `${API_BASE_URL}/ituran-vehicle-history.php?plate=${encodeURIComponent(vehicle.plate)}&date=${date}&pointsMode=none`
      );
      const data = await response.json();

      if (data.success && data.data.trips && data.data.trips.length > 0) {
        const trips = data.data.trips.map((ituranTrip: any) => ({
          trip_id: ituranTrip.trip_id,
          points: [], // Will be loaded on demand
          start_time: ituranTrip.start_location?.timestamp || '',
          end_time: ituranTrip.end_location?.timestamp || '',
          start_location: ituranTrip.start_location?.location?.address?.location || '',
          end_location: ituranTrip.end_location?.location?.address?.location || '',
          total_distance: ituranTrip.distance || 0, // Already in km from API
          max_speed: ituranTrip.max_speed || 0,
          avg_speed: ituranTrip.avg_speed || 0,
          duration: ituranTrip.duration_in_seconds || 0,
        }));
        
        setDetectedTrips(trips);
        
        // Load first trip's points immediately
        if (trips.length > 0) {
          await loadTripPoints(trips[0].trip_id, 0);
        }
      } else {
        alert('No trips found for this date');
      }
    } catch (err) {
      console.error('Failed to load trip data:', err);
      alert('Failed to load trip data');
    } finally {
      setIsLoadingTrip(false);
    }
  };
  
  // Load points for a specific trip
  const loadTripPoints = async (tripId: string, tripIndex: number) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/ituran-trip-points.php?trip_id=${encodeURIComponent(tripId)}`
      );
      const data = await response.json();

      if (data.success && data.data.points && data.data.points.length > 0) {
        // Create the updated trip with points
        const updatedTrip = {
          ...detectedTrips[tripIndex],
          points: data.data.points
        };
        
        // Update the trip with its points
        setDetectedTrips(prevTrips => {
          const updatedTrips = [...prevTrips];
          updatedTrips[tripIndex] = updatedTrip;
          return updatedTrips;
        });
        
        // Set as active trip with the updated trip object
        setSelectedTrip(tripIndex);
        setTripData(updatedTrip);
        setCurrentIndex(0);
        setIsReplayMode(true);
        setIsPlaying(false);
      } else {
        alert('No points found for this trip');
      }
    } catch (err) {
      console.error('Failed to load trip points:', err);
      alert('Failed to load trip points');
    }
  };

  // Start trip replay
  const startTripReplay = (vehicle: IturanVehicle) => {
    setReplayVehicle(vehicle);
    fetchTripData(vehicle, selectedDate);
  };

  // Close trip replay
  const closeTripReplay = () => {
    setIsReplayMode(false);
    setReplayVehicle(null);
    setTripData(null);
    setIsPlaying(false);
    setCurrentIndex(0);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  // Dashcam video functions
  const openLiveVideo = async (vehicle: IturanVehicle, channel?: number) => {
    const mapping = dashcamMappings[vehicle.plate];
    if (!mapping) {
      alert('No dashcam found for this vehicle');
      return;
    }

    const channelToUse = channel || mapping.default_channel || 1;

    setIsLoadingLiveVideo(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/mettax-live-video.php?vehicle_plate=${encodeURIComponent(vehicle.plate)}&channel_id=${channelToUse}`
      );
      const data = await response.json();

      if (data.success) {
        setVideoModalData({
          vehicle,
          type: 'live',
          url: data.video_url,
          currentChannel: channelToUse
        });
        setShowVideoModal(true);
      } else {
        alert('Failed to get live video: ' + (data.error || data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Failed to get live video:', err);
      alert('Failed to get live video');
    } finally {
      setIsLoadingLiveVideo(false);
    }
  };

  const openHistoricalVideo = async (vehicle: IturanVehicle, startTime: string, endTime: string, channel?: number) => {
    const mapping = dashcamMappings[vehicle.plate];
    if (!mapping) {
      alert('No dashcam found for this vehicle');
      return;
    }

    const channelToUse = channel || mapping.default_channel || 1;

    setIsLoadingHistoricalVideo(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/mettax-historical-video.php?vehicle_plate=${encodeURIComponent(vehicle.plate)}&start_time=${encodeURIComponent(startTime)}&end_time=${encodeURIComponent(endTime)}&channel_id=${channelToUse}`
      );
      const data = await response.json();

      if (data.success) {
        setVideoModalData({
          vehicle,
          type: 'historical',
          url: data.video_url,
          startTime,
          endTime,
          currentChannel: channelToUse
        });
        setShowVideoModal(true);
      } else {
        alert('Failed to get historical video: ' + (data.error || data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Failed to get historical video:', err);
      alert('Failed to get historical video');
    } finally {
      setIsLoadingHistoricalVideo(false);
    }
  };

  const sendCommand = async (command: string, params: any = {}) => {
    if (!commandVehicle) return;

    setIsLoadingCommand(true);
    try {
      const response = await fetch(`${API_BASE_URL}/ituran-send-command.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plate: commandVehicle.plate,
          command,
          params
        })
      });

      const data = await response.json();

      if (data.success) {
        const result = data.result || {};
        const statusCode = result.status_code;
        const statusMsg = result.status_message || 'Command sent';
        
        let message = `✓ Command "${command}" sent to ${commandVehicle.plate}\n\n`;
        
        if (statusCode === 0) {
          message += '⏳ Status: Waiting for device response...';
        } else if (statusCode === 200) {
          message += '✓ Status: Command executed successfully!';
        } else if (statusCode === 400) {
          message += '✗ Status: Command failed';
        } else if (statusCode === 408) {
          message += '⏱ Status: Command timeout';
        } else {
          message += `Status: ${statusMsg}`;
        }
        
        alert(message);
        setShowCommandModal(false);
        
        // Refresh vehicle data after successful command
        if (statusCode === 200) {
          setTimeout(() => fetchLiveTracking(), 2000);
        }
      } else {
        alert('Failed to send command: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Failed to send command:', err);
      alert('Failed to send command: Network error');
    } finally {
      setIsLoadingCommand(false);
    }
  };

  const switchChannel = async (newChannel: number) => {
    if (!videoModalData) return;
    
    const mapping = dashcamMappings[videoModalData.vehicle.plate];
    if (!mapping) return;

    // Clear the current URL to force iframe reload
    setVideoModalData({
      ...videoModalData,
      url: undefined,
      currentChannel: newChannel
    });

    if (videoModalData.type === 'live') {
      setIsLoadingLiveVideo(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/mettax-live-video.php?vehicle_plate=${encodeURIComponent(videoModalData.vehicle.plate)}&channel_id=${newChannel}`
        );
        const data = await response.json();

        if (data.success) {
          setVideoModalData({
            vehicle: videoModalData.vehicle,
            type: 'live',
            url: data.video_url,
            currentChannel: newChannel
          });
        } else {
          alert('Failed to switch channel: ' + (data.error || data.message || 'Unknown error'));
          // Restore previous channel on error
          setVideoModalData(videoModalData);
        }
      } catch (err) {
        console.error('Failed to switch channel:', err);
        alert('Failed to switch channel');
        // Restore previous channel on error
        setVideoModalData(videoModalData);
      } finally {
        setIsLoadingLiveVideo(false);
      }
    } else if (videoModalData.type === 'historical' && videoModalData.startTime && videoModalData.endTime) {
      setIsLoadingHistoricalVideo(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/mettax-historical-video.php?vehicle_plate=${encodeURIComponent(videoModalData.vehicle.plate)}&start_time=${encodeURIComponent(videoModalData.startTime)}&end_time=${encodeURIComponent(videoModalData.endTime)}&channel_id=${newChannel}`
        );
        const data = await response.json();

        if (data.success) {
          setVideoModalData({
            vehicle: videoModalData.vehicle,
            type: 'historical',
            url: data.video_url,
            startTime: videoModalData.startTime,
            endTime: videoModalData.endTime,
            currentChannel: newChannel
          });
        } else {
          alert('Failed to switch channel: ' + (data.error || data.message || 'Unknown error'));
          // Restore previous channel on error
          setVideoModalData(videoModalData);
        }
      } catch (err) {
        console.error('Failed to switch channel:', err);
        alert('Failed to switch channel');
        // Restore previous channel on error
        setVideoModalData(videoModalData);
      } finally {
        setIsLoadingHistoricalVideo(false);
      }
    }
  };

  const closeVideoModal = () => {
    setShowVideoModal(false);
    setVideoModalData(null);
  };

  // Playback control
  React.useEffect(() => {
    if (isPlaying && tripData && currentIndex < tripData.points.length - 1) {
      intervalRef.current = window.setInterval(() => {
        setCurrentIndex((prev) => {
          if (prev >= tripData.points.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, 1000 / playbackSpeed);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying, currentIndex, tripData, playbackSpeed]);

  // Format time since last update
  const formatTimeSince = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  };

  if (loading && vehicles.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="relative h-screen flex">
      {/* Custom popup styles */}
      <style>{`
        .leaflet-popup-content-wrapper {
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.15), 0 3px 12px rgba(0,0,0,0.1);
          padding: 0;
          overflow: hidden;
        }
        .leaflet-popup-content {
          margin: 16px;
          font-family: inherit;
        }
        .leaflet-popup-tip {
          box-shadow: 0 3px 14px rgba(0,0,0,0.15);
        }
        .custom-popup .leaflet-popup-close-button {
          top: 8px;
          right: 8px;
          width: 24px;
          height: 24px;
          font-size: 20px;
          color: #6b7280;
          padding: 0;
        }
        .custom-popup .leaflet-popup-close-button:hover {
          color: #1f2937;
        }
      `}</style>
      
      {/* Vehicle Sidebar - Responsive with toggle */}
      <div className={`${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 fixed lg:relative w-80 bg-white border-r shadow-lg overflow-hidden flex flex-col z-40 transition-transform duration-300 ease-in-out h-full`}>
        {/* Sidebar Header */}
        <div className="p-4 border-b bg-fleet-secondary">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-white" />
              <h2 className="text-lg font-bold text-white">
                Fleet Vehicles ({vehicles.length})
              </h2>
            </div>
            {/* Close button - only visible on mobile */}
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 hover:bg-fleet-primary/20 rounded transition-colors"
              aria-label="Close vehicle list"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
          <div className="mt-2 text-xs text-white opacity-80">
            {lastUpdate && `Updated ${lastUpdate.toLocaleTimeString()}`}
          </div>
        </div>

        {/* Vehicle List Header with Count */}
        <div className="p-3 border-b bg-gray-50">
          <div className="text-xs font-semibold text-gray-600">
            Vehicles List
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="mx-3 mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
            {error}
          </div>
        )}

        {/* Vehicle List */}
        <div className="flex-1 overflow-y-auto">
          {(() => {
            if (filteredVehicles.length === 0) {
              return (
                <div className="p-4 text-center text-gray-500 text-sm">
                  {vehicles.length === 0 ? 'No vehicles found' : 'No vehicles match your filters'}
                </div>
              );
            }

            return (
              <div className="divide-y">
                {filteredVehicles.map((vehicle) => {
                  // Determine vehicle status color (same as marker)
                  const statusColor = vehicle.has_alert ? 'bg-red-600' :
                    vehicle.ignition && vehicle.speed > 5 ? 'bg-fleet-primary' :
                    vehicle.ignition && vehicle.speed <= 5 ? 'bg-yellow-500' :
                    'bg-gray-400';
                  
                  const isMoving = vehicle.ignition && vehicle.speed > 5;
                  
                  return (
                <div
                  key={vehicle.vehicle_id}
                  onClick={() => setSelectedVehicle(vehicle)}
                  className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedVehicle?.vehicle_id === vehicle.vehicle_id ? 'bg-fleet-light border-l-4 border-fleet-primary' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {/* Status indicator with same color as map marker */}
                        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${statusColor} ${
                          vehicle.has_alert ? 'animate-pulse' : ''
                        }`}></div>
                        
                        <div className="font-semibold text-sm text-gray-900 truncate">
                          {vehicle.name || vehicle.plate}
                        </div>
                      </div>
                      
                      {vehicle.driver && (
                        <div className="text-xs text-gray-600 mt-1 flex items-center gap-1 ml-6">
                          <Users className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="truncate">{vehicle.driver}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 ml-6">
                        <span className="flex items-center gap-1.5">
                          {/* Direction arrow with rotation and color matching status - LARGER AND MORE VISIBLE */}
                          <Navigation 
                            className={`w-4 h-4 flex-shrink-0 ${
                              isMoving ? 'text-fleet-primary' : 'text-gray-400'
                            }`}
                            style={{
                              transform: `rotate(${vehicle.heading}deg)`,
                              transition: 'transform 0.3s ease'
                            }}
                          />
                          <span className="whitespace-nowrap font-medium">{vehicle.speed.toFixed(0)} km/h</span>
                        </span>
                        {vehicle.vehicle_type && (
                          <span className="capitalize px-2 py-0.5 bg-gray-100 rounded text-xs whitespace-nowrap">
                            {vehicle.vehicle_type}
                          </span>
                        )}
                      </div>
                      
                      <div className="text-xs text-gray-400 mt-1 ml-6">
                        {formatTimeSince(vehicle.seconds_since_update)}
                      </div>
                    </div>
                  </div>
                </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Map Container */}
      <div className="flex-1 relative">
        <MapContainer
          center={mapCenter}
          zoom={12}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution={MAP_TILES[currentTile].attribution}
            url={MAP_TILES[currentTile].url}
          />
          
          {/* Roads overlay - Always visible detailed road network */}
          <TileLayer
            url="https://{s}.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png"
            attribution='<a href="https://github.com/cyclosm/cyclosm-cartocss-style/releases" title="CyclOSM - Open Bicycle render">CyclOSM</a> | Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            opacity={0.3}
          />

          {/* Fit bounds to all vehicles on initial load or when reset */}
          <FitBoundsToVehicles vehicles={vehicles} trigger={resetMapTrigger} />
          
          {/* Pan to selected vehicle */}
          <PanToSelectedVehicle vehicle={selectedVehicle} autoFollow={autoFollow} />

          {/* Render vehicle markers */}
          {filteredVehicles.map((vehicle) => (
            <Marker
              key={vehicle.vehicle_id}
              position={[vehicle.position.lat, vehicle.position.lng]}
              icon={createIturanVehicleIcon(
                vehicle.connection_status,
                vehicle.speed,
                vehicle.vehicle_type || 'truck',
                vehicle.ignition || false,
                selectedVehicle?.vehicle_id === vehicle.vehicle_id,
                vehicle.has_alert || false
              )}
              eventHandlers={{
                click: () => setSelectedVehicle(vehicle),
              }}
            >
              <Popup maxWidth={400} className="custom-popup">
                <div className="min-w-[320px]">
                  {/* Header with vehicle plate and status badge */}
                  <div className="mb-3 pb-3 border-b border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-xl text-gray-900">{vehicle.plate}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        vehicle.has_alert ? 'bg-red-100 text-red-700' :
                        vehicle.ignition && vehicle.speed > 5 ? 'bg-fleet-primary text-white' :
                        vehicle.ignition && vehicle.speed <= 5 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {vehicle.has_alert ? '⚠️ ALERT' :
                         vehicle.ignition && vehicle.speed > 5 ? '� MOVING' :
                         vehicle.ignition && vehicle.speed <= 5 ? '🟡 IDLING' :
                         '⚫ STOPPED'}
                      </span>
                    </div>
                    {vehicle.driver && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Users className="w-4 h-4" />
                        <span>{vehicle.driver}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Vehicle details grid */}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-gray-500 uppercase tracking-wide">Type</span>
                      <span className="text-sm font-medium capitalize flex items-center gap-1">
                        <Truck className="w-4 h-4 text-gray-400" />
                        {vehicle.vehicle_type || 'truck'}
                      </span>
                    </div>

                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-gray-500 uppercase tracking-wide">Ignition</span>
                      <span className={`text-sm font-medium flex items-center gap-1 ${
                        vehicle.ignition ? 'text-red-600' : 'text-gray-600'
                      }`}>
                        <Zap className="w-4 h-4" />
                        {vehicle.ignition ? 'ON' : 'OFF'}
                      </span>
                    </div>

                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-gray-500 uppercase tracking-wide">Speed</span>
                      <span className="text-sm font-medium flex items-center gap-1">
                        <Navigation className="w-4 h-4 text-fleet-secondary" />
                        {vehicle.speed.toFixed(1)} km/h
                      </span>
                    </div>

                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-gray-500 uppercase tracking-wide">Mileage</span>
                      <span className="text-sm font-medium flex items-center gap-1">
                        <TrendingUp className="w-4 h-4 text-purple-500" />
                        {vehicle.mileage.toFixed(1)} km
                      </span>
                    </div>
                  </div>

                  {/* Location */}
                  {vehicle.address && (
                    <div className="mb-3 p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-500" />
                        <div>
                          <div className="text-xs text-gray-500 mb-0.5">Current Location</div>
                          <div className="text-sm text-gray-700">{vehicle.address}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Active Events/Alerts */}
                  {vehicle.events && vehicle.events.length > 0 && (
                    <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-600" />
                        <div className="flex-1">
                          <div className="text-xs font-semibold text-red-700 mb-1">Active Alerts</div>
                          {vehicle.events.map((event, idx) => (
                            <div key={idx} className="text-xs text-red-600 mb-1 last:mb-0">
                              • {event.event_type}
                              {event.timestamp && (
                                <span className="text-red-500 ml-1">
                                  ({new Date(event.timestamp).toLocaleTimeString()})
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Last update */}
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-3 pb-3 border-b">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Last update: {formatTimeSince(vehicle.seconds_since_update)}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <button 
                        onClick={() => startTripReplay(vehicle)}
                        disabled={isLoadingTrip}
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-fleet-secondary text-white text-sm font-medium rounded-lg hover:opacity-80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoadingTrip ? (
                          <>
                            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Loading...
                          </>
                        ) : (
                          <>
                            <Play className="w-4 h-4" />
                            Replay Trip
                          </>
                        )}
                      </button>
                      <button 
                        onClick={() => {
                          setCommandVehicle(vehicle);
                          setShowCommandModal(true);
                        }}
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                        </svg>
                        Commands
                      </button>
                    </div>
                    
                    {/* Dashcam buttons */}
                    {dashcamMappings[vehicle.plate] && (
                      <div className="grid grid-cols-2 gap-2 pt-2 border-t">
                        <button 
                          onClick={() => openLiveVideo(vehicle)}
                          disabled={isLoadingLiveVideo}
                          className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isLoadingLiveVideo ? (
                            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          )}
                          {isLoadingLiveVideo ? 'Loading...' : 'Live Video'}
                        </button>
                        <button 
                          onClick={() => {
                            const today = new Date();
                            const startTime = new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString().slice(0, 19).replace('T', ' ');
                            const endTime = new Date().toISOString().slice(0, 19).replace('T', ' ');
                            openHistoricalVideo(vehicle, startTime, endTime);
                          }}
                          disabled={isLoadingHistoricalVideo}
                          className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isLoadingHistoricalVideo ? (
                            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          )}
                          {isLoadingHistoricalVideo ? 'Loading...' : 'Watch Footage'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Render geofences */}
          {showGeofences && geofences.map((geofence) => {
            if (geofence.type === 'circle' && geofence.geometry.coordinates) {
              const [lng, lat] = geofence.geometry.coordinates as number[];
              return (
                <Circle
                  key={`geofence-${geofence.id}`}
                  center={[lat, lng]}
                  radius={geofence.radius || 100}
                  pathOptions={{
                    color: geofence.color,
                    fillColor: geofence.color,
                    fillOpacity: 0.2,
                    weight: 2
                  }}
                >
                  <Popup>
                    <div className="p-2">
                      <h3 className="font-bold text-sm mb-1">{geofence.name}</h3>
                      {geofence.description && (
                        <p className="text-xs text-gray-600">{geofence.description}</p>
                      )}
                      <div className="text-xs text-gray-500 mt-1">
                        Radius: {geofence.radius}m
                      </div>
                    </div>
                  </Popup>
                </Circle>
              );
            } else if (geofence.type === 'polygon' && geofence.geometry.coordinates) {
              const positions = (geofence.geometry.coordinates as number[][]).map(
                ([lng, lat]) => [lat, lng] as [number, number]
              );
              return (
                <Polygon
                  key={`geofence-${geofence.id}`}
                  positions={positions}
                  pathOptions={{
                    color: geofence.color,
                    fillColor: geofence.color,
                    fillOpacity: 0.2,
                    weight: 2
                  }}
                >
                  <Popup>
                    <div className="p-2">
                      <h3 className="font-bold text-sm mb-1">{geofence.name}</h3>
                      {geofence.description && (
                        <p className="text-xs text-gray-600">{geofence.description}</p>
                      )}
                    </div>
                  </Popup>
                </Polygon>
              );
            }
            return null;
          })}
        </MapContainer>



        {/* Vehicle count overlay */}
        <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg px-3 py-2 z-[1000]">
          <div className="text-sm text-gray-600">
            Showing <span className="font-bold text-gray-900">{filteredVehicles.length}</span> of <span className="font-bold text-gray-900">{vehicles.length}</span> vehicle{vehicles.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Vertical Map Controls */}
        <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-2">
          {/* Menu Toggle - Only visible on mobile, first position */}
          <button
            onClick={() => setSidebarOpen(true)}
            className={`lg:hidden bg-white rounded-lg shadow-lg p-3 hover:bg-gray-50 transition-colors ${sidebarOpen ? 'hidden' : ''}`}
            title="Show vehicle list"
          >
            <Truck className="w-5 h-5 text-gray-700" />
          </button>

          {/* Filters Toggle */}
          <button
            onClick={() => setIsOverlayExpanded(!isOverlayExpanded)}
            className={`rounded-lg shadow-lg p-3 transition-colors ${
              isOverlayExpanded 
                ? 'bg-fleet-primary text-white hover:bg-fleet-primary' 
                : 'bg-white hover:bg-gray-50'
            }`}
            title="Toggle filters"
          >
            <Filter className={`w-5 h-5 ${isOverlayExpanded ? 'text-white' : 'text-gray-700'}`} />
          </button>

          {/* Map Layer Switcher */}
          <div className="relative map-switcher-container">
            <button
              onClick={() => setShowMapSwitcher(!showMapSwitcher)}
              className={`rounded-lg shadow-lg p-3 transition-colors ${
                showMapSwitcher 
                  ? 'bg-fleet-primary text-white hover:bg-fleet-primary' 
                  : 'bg-white hover:bg-gray-50'
              }`}
              title="Change map layers"
            >
              <Map className={`w-5 h-5 ${showMapSwitcher ? 'text-white' : 'text-gray-700'}`} />
            </button>
            
            {showMapSwitcher && (
              <div className="absolute top-0 right-14 bg-white rounded-lg shadow-lg border min-w-48 z-[1001]">
                <div className="p-3 border-b">
                  <h3 className="font-semibold text-gray-900 text-sm">Map Layers</h3>
                </div>
                
                {/* Map Tiles */}
                <div className="p-3">
                  <div className="text-xs font-medium text-gray-600 mb-2">Base Map:</div>
                  <div className="space-y-1">
                    {Object.entries(MAP_TILES).map(([key, tile]) => (
                      <label key={key} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="radio"
                          name="mapTile"
                          value={key}
                          checked={currentTile === key}
                          onChange={(e) => setCurrentTile(e.target.value as keyof typeof MAP_TILES)}
                          className="text-fleet-primary"
                        />
                        <span className="text-sm text-gray-700">{tile.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Geofences Toggle */}
          <div className="flex flex-col gap-0">
            {geofences.length > 0 && (
              <div className="bg-fleet-primary text-white text-xs font-bold rounded-t-lg px-3 py-1 text-center shadow-lg">
                {geofences.length}
              </div>
            )}
            <button
              onClick={() => {
                const newState = !showGeofences;
                setShowGeofences(newState);
                console.log(`Geofences ${newState ? 'enabled' : 'disabled'} - ${geofences.length} geofences available`);
              }}
              className={`${geofences.length > 0 ? 'rounded-b-lg' : 'rounded-lg'} shadow-lg p-3 transition-colors ${
                showGeofences 
                  ? 'bg-fleet-primary text-white hover:bg-fleet-primary' 
                  : 'bg-white hover:bg-gray-50'
              }`}
              title={showGeofences ? 'Hide geofences' : 'Show geofences'}
            >
              <Target className={`w-5 h-5 ${
                showGeofences ? 'text-white' : 'text-gray-700'
              }`} />
            </button>
          </div>

          {/* Alerts Toggle */}
          <button
            onClick={() => setStatusFilter(statusFilter === 'alert' ? 'all' : 'alert')}
            className={`rounded-lg shadow-lg p-3 transition-colors ${
              statusFilter === 'alert' 
                ? 'bg-red-100 border-2 border-red-300' 
                : 'bg-white hover:bg-gray-50'
            }`}
            title={statusFilter === 'alert' ? 'Show all vehicles' : 'Show only vehicles with alerts'}
          >
            <AlertTriangle className={`w-5 h-5 ${
              statusFilter === 'alert' ? 'text-red-600' : 'text-red-500'
            }`} />
          </button>
          
          {/* Divider */}
          <div className="border-t border-gray-300 my-1"></div>
          
          {/* Safety Dashboard */}
          <button
            onClick={() => setShowSafetyModal(true)}
            className="bg-white rounded-lg shadow-lg p-3 hover:bg-gray-50 transition-colors"
            title="Safety Dashboard"
          >
            <Shield className="w-5 h-5 text-green-600" />
          </button>

          {/* Analytics Dashboard */}
          <button
            onClick={() => setShowAnalyticsModal(true)}
            className="bg-white rounded-lg shadow-lg p-3 hover:bg-gray-50 transition-colors"
            title="Analytics Dashboard"
          >
            <BarChart3 className="w-5 h-5 text-blue-600" />
          </button>

          {/* Geofence Monitor */}
          <button
            onClick={() => setShowGeofenceModal(true)}
            className="bg-white rounded-lg shadow-lg p-3 hover:bg-gray-50 transition-colors"
            title="Geofence Monitor"
          >
            <MapPin className="w-5 h-5 text-purple-600" />
          </button>

          {/* Events Timeline */}
          <button
            onClick={() => setShowEventsModal(true)}
            className="bg-white rounded-lg shadow-lg p-3 hover:bg-gray-50 transition-colors"
            title="Events Timeline"
          >
            <Activity className="w-5 h-5 text-orange-600" />
          </button>

          {/* Vehicle Search */}
          <button
            onClick={() => setShowVehicleSearch(true)}
            className="bg-white rounded-lg shadow-lg p-3 hover:bg-gray-50 transition-colors"
            title="Advanced Vehicle Search"
          >
            <Search className="w-5 h-5 text-teal-600" />
          </button>
          
          {/* Divider */}
          <div className="border-t border-gray-300 my-1"></div>

          {/* Reset Map View */}
          <button
            onClick={() => {
              setResetMapTrigger(prev => prev + 1);
              setSelectedVehicle(null);
              setAutoFollow(false);
            }}
            className="bg-white rounded-lg shadow-lg p-3 hover:bg-gray-50 transition-colors"
            title="Reset map view to show all vehicles"
          >
            <Home className="w-5 h-5 text-purple-600" />
          </button>

          {/* Auto-follow Toggle */}
          <button
            onClick={() => setAutoFollow(!autoFollow)}
            className={`rounded-lg shadow-lg p-3 transition-colors ${
              autoFollow 
                ? 'bg-fleet-primary text-white hover:bg-fleet-primary' 
                : 'bg-white hover:bg-gray-50'
            }`}
            title={autoFollow ? 'Disable auto-follow' : 'Enable auto-follow selected vehicle'}
          >
            <Eye className={`w-5 h-5 ${
              autoFollow ? 'text-white' : 'text-gray-700'
            }`} />
          </button>
        </div>

        {/* Expanded Filters Panel */}
        {isOverlayExpanded && (
          <div className="absolute top-4 right-20 bg-white rounded-lg shadow-xl border min-w-80 z-[999]">
            <div className="p-4 border-b border-gray-200 bg-fleet-primary text-white rounded-t-lg">
              <h3 className="font-semibold text-lg">Fleet Controls</h3>
            </div>

            {/* Status Legend */}
            <div className="p-4 border-b bg-gray-50">
              <div className="text-xs font-semibold text-gray-700 mb-2">Status Legend:</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-fleet-primary"></div>
                  <span>Moving</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <span>Idling</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                  <span>Stopped</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span>Alert</span>
                </div>
              </div>
            </div>
            
            {/* Quick Search */}
            <div className="p-4 border-b border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Vehicles
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Plate or driver..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="p-4 border-b border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
              >
                <option value="all">All Statuses</option>
                <option value="moving">Moving</option>
                <option value="idling">Idling</option>
                <option value="stopped">Stopped</option>
                <option value="alert">Alert</option>
              </select>
            </div>

            {/* Vehicle Type Filter */}
            <div className="p-4 border-b border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Type
              </label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
              >
                <option value="all">All Types</option>
                <option value="truck">Truck</option>
                <option value="van">Van</option>
                <option value="car">Car</option>
                <option value="bus">Bus</option>
              </select>
            </div>

            {/* Toggles */}
            <div className="p-4 border-b border-gray-200 space-y-3">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showOnlineOnly}
                  onChange={(e) => setShowOnlineOnly(e.target.checked)}
                  className="rounded text-fleet-primary focus:ring-fleet-primary"
                />
                Show online only
              </label>
              
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showGeofences}
                  onChange={(e) => setShowGeofences(e.target.checked)}
                  className="rounded text-fleet-primary focus:ring-fleet-primary"
                />
                Show geofences ({geofences.length})
              </label>

              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="rounded text-fleet-primary focus:ring-fleet-primary"
                />
                Auto-refresh
                {autoRefresh && (
                  <select
                    value={refreshInterval}
                    onChange={(e) => setRefreshInterval(Number(e.target.value))}
                    className="text-xs border rounded px-2 py-1 ml-auto"
                  >
                    <option value="5">5s</option>
                    <option value="10">10s</option>
                    <option value="30">30s</option>
                    <option value="60">60s</option>
                  </select>
                )}
              </label>
            </div>

            {/* Geofences Summary */}
            {showGeofences && geofences.length > 0 && (
              <div className="p-4 border-b border-gray-200">
                <div className="text-xs font-semibold text-gray-700 mb-2">Geofences Active:</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full border-2 border-blue-500"></div>
                    <span>Circles: {geofences.filter(g => g.type === 'circle').length}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 border-2 border-green-500"></div>
                    <span>Polygons: {geofences.filter(g => g.type === 'polygon').length}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="p-4">
              <div className="grid grid-cols-1 gap-2">
                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-fleet-primary text-white rounded-lg hover:bg-fleet-secondary transition-colors text-sm"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setStatusFilter('all');
                    setTypeFilter('all');
                    setShowOnlineOnly(false);
                  }}
                  className="px-3 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Trip Replay Modal */}
      {isReplayMode && tripData && replayVehicle && (
        <div className="absolute inset-0 bg-black/50 z-[2000] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-4 py-2.5 border-b bg-fleet-secondary">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-white">
                    Trip Replay - {replayVehicle.plate}
                    {replayVehicle.name && replayVehicle.name !== replayVehicle.plate && (
                      <span className="text-white opacity-80 font-normal ml-2">({replayVehicle.name})</span>
                    )}
                  </h2>
                  <p className="text-white opacity-80 text-sm">
                    {detectedTrips.length} trip{detectedTrips.length !== 1 ? 's' : ''} detected on {selectedDate}
                  </p>
                </div>
                <button
                  onClick={closeTripReplay}
                  className="text-white hover:bg-white/20 rounded-lg p-1.5 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content - Full width controls with map */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-4">
                {/* Date and Trip Selector - Inline */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {/* Date Selector */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Select Date</label>
                    <div className="flex gap-2">
                      <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                        className="flex-1 px-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={isLoadingTrip}
                      />
                      <button
                        onClick={() => fetchTripData(replayVehicle, selectedDate)}
                        disabled={isLoadingTrip}
                        className="px-4 py-1.5 text-sm bg-fleet-secondary text-white rounded-lg hover:opacity-80 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {isLoadingTrip ? (
                          <>
                            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Loading
                          </>
                        ) : (
                          'Load'
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Trip Selector */}
                  {detectedTrips.length > 1 ? (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Select Trip</label>
                      <select
                        value={selectedTrip}
                        onChange={async (e) => {
                          const tripIndex = parseInt(e.target.value);
                          const selectedTripData = detectedTrips[tripIndex];
                          
                          // Check if trip already has points loaded
                          if (selectedTripData.points && selectedTripData.points.length > 0) {
                            // Points already loaded, just switch
                            setSelectedTrip(tripIndex);
                            setTripData(selectedTripData);
                            setCurrentIndex(0);
                            setIsPlaying(false);
                          } else {
                            // Need to load points for this trip
                            setIsPlaying(false);
                            await loadTripPoints(selectedTripData.trip_id, tripIndex);
                          }
                        }}
                        className="w-full px-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {detectedTrips.map((trip: any, index: number) => (
                          <option key={index} value={index}>
                            Trip {index + 1}: {new Date(trip.start_time).toLocaleTimeString()} - {(trip.total_distance || 0).toFixed(1)} km
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : <div></div>}
                </div>

                {/* Map and Stats in 2 columns */}
                <div className="grid grid-cols-[60%_40%] gap-4 mb-4">
                  {/* Mini Map */}
                  <div className="bg-gray-100 rounded-xl overflow-hidden h-[500px] relative">
                    <MapContainer
                      center={[tripData.points[0].latitude, tripData.points[0].longitude]}
                      zoom={14}
                      className="h-full w-full"
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; OpenStreetMap'
                      />
                      
                      {/* Keep marker in view only when out of bounds */}
                      <KeepMarkerInView 
                        lat={tripData.points[currentIndex].latitude} 
                        lng={tripData.points[currentIndex].longitude} 
                      />
                      
                      {/* Route Line */}
                      <Polyline
                        positions={tripData.points.map((p: any) => [p.latitude, p.longitude])}
                        pathOptions={{ color: 'blue', weight: 3, opacity: 0.6 }}
                      />
                      
                      {/* Start Marker */}
                      <Circle
                        center={[tripData.points[0].latitude, tripData.points[0].longitude]}
                        radius={30}
                        pathOptions={{ color: 'green', fillColor: 'green', fillOpacity: 0.5 }}
                      />
                      
                      {/* End Marker */}
                      <Circle
                        center={[
                          tripData.points[tripData.points.length - 1].latitude,
                          tripData.points[tripData.points.length - 1].longitude
                        ]}
                        radius={30}
                        pathOptions={{ color: 'red', fillColor: 'red', fillOpacity: 0.5 }}
                      />
                      
                      {/* Current Position - Direction-Aware Arrow */}
                      <Marker
                        position={[tripData.points[currentIndex].latitude, tripData.points[currentIndex].longitude]}
                        icon={createReplayArrowIcon(
                          tripData.points[currentIndex].heading || 0,
                          tripData.points[currentIndex].speed || 0,
                          false // Set to true if point has an event/alert
                        )}
                      >
                        <Popup>
                          <div className="text-sm">
                            <div><strong>Time:</strong> {new Date(tripData.points[currentIndex].timestamp).toLocaleTimeString()}</div>
                            <div><strong>Speed:</strong> {Math.round(tripData.points[currentIndex].speed)} km/h</div>
                            <div><strong>Heading:</strong> {Math.round(tripData.points[currentIndex].heading || 0)}°</div>
                            {tripData.points[currentIndex].address && (
                              <div><strong>Location:</strong> {tripData.points[currentIndex].address}</div>
                            )}
                          </div>
                        </Popup>
                      </Marker>
                    </MapContainer>
                    
                    {/* Speed Overlay */}
                    <div className="absolute top-2 right-2 bg-black/80 text-white px-3 py-2 rounded-lg shadow-lg">
                      <div className="text-3xl font-bold leading-none">{Math.round(tripData.points[currentIndex].speed)}</div>
                      <div className="text-[10px] text-gray-300 text-center">km/h</div>
                    </div>
                  </div>

                  {/* Stats and Current Position */}
                  <div className="space-y-3">
                    {/* Playback Controls - Compact */}
                    <div className="bg-white border border-gray-200 rounded-lg p-2">
                      <div className="flex items-center justify-center gap-1.5 mb-2">
                        <button
                          onClick={() => setCurrentIndex(0)}
                          className="p-1.5 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                          title="Reset to start"
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setCurrentIndex(Math.max(0, currentIndex - 10))}
                          className="p-1.5 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                          title="Back 10 points"
                        >
                          <SkipBack className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setIsPlaying(!isPlaying)}
                          className="p-2 bg-fleet-secondary hover:opacity-80 text-white rounded transition-colors shadow-md"
                          title={isPlaying ? 'Pause' : 'Play'}
                        >
                          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => setCurrentIndex(Math.min(tripData.points.length - 1, currentIndex + 10))}
                          className="p-1.5 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                          title="Forward 10 points"
                        >
                          <SkipForward className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setCurrentIndex(tripData.points.length - 1)}
                          className="p-1.5 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                          title="Jump to end"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>

                      {/* Speed Controls */}
                      <div>
                        <label className="block text-[9px] font-semibold text-gray-700 mb-1 text-center">
                          Playback Speed: {playbackSpeed}x
                        </label>
                        <div className="flex gap-1">
                          {[0.5, 1, 2, 5, 10].map((speed) => (
                            <button
                              key={speed}
                              onClick={() => setPlaybackSpeed(speed)}
                              className={`flex-1 px-2 py-1 text-[10px] font-medium rounded transition-colors ${
                                playbackSpeed === speed
                                  ? 'bg-fleet-secondary text-white shadow-sm'
                                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                              }`}
                            >
                              {speed}x
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Trip Stats Grid */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-gray-100 p-3 rounded-lg text-center">
                        <div className="text-[10px] text-fleet-secondary font-semibold mb-1">Distance</div>
                        <div className="text-2xl font-bold text-fleet-dark">{(tripData.total_distance || 0).toFixed(1)}</div>
                        <div className="text-[10px] text-fleet-secondary">km</div>
                      </div>
                      <div className="bg-gray-100 p-3 rounded-lg text-center">
                        <div className="text-[10px] text-fleet-secondary font-semibold mb-1">Duration</div>
                        <div className="text-2xl font-bold text-fleet-dark">{Math.round((tripData.duration || 0) / 60)}</div>
                        <div className="text-[10px] text-fleet-secondary">minutes</div>
                      </div>
                      <div className="bg-gray-100 p-3 rounded-lg text-center">
                        <div className="text-[10px] text-red-600 font-semibold mb-1">Max Speed</div>
                        <div className="text-2xl font-bold text-red-900">{Math.round(tripData.max_speed || 0)}</div>
                        <div className="text-[10px] text-red-600">km/h</div>
                      </div>
                      <div className="bg-gray-100 p-3 rounded-lg text-center">
                        <div className="text-[10px] text-fleet-secondary font-semibold mb-1">Avg Speed</div>
                        <div className="text-2xl font-bold text-fleet-dark">{Math.round(tripData.avg_speed || 0)}</div>
                        <div className="text-[10px] text-fleet-secondary">km/h</div>
                      </div>
                    </div>

                    {/* Current Position Info */}
                    <div className="bg-gray-50 rounded-lg p-3">
                      <h3 className="text-xs font-semibold text-gray-700 mb-2">Current Position</h3>
                      <div className="space-y-1.5 text-xs">
                        <div className="flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-gray-600">
                            {new Date(tripData.points[currentIndex].timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Navigation className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-gray-600">
                            Speed: {Math.round(tripData.points[currentIndex].speed)} km/h
                          </span>
                        </div>
                        {tripData.points[currentIndex].address && (
                          <div className="flex items-start gap-2">
                            <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5" />
                            <span className="text-gray-600 text-[11px] leading-tight">{tripData.points[currentIndex].address}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideoModal && videoModalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex-1">
                <div className="flex items-center gap-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      {videoModalData.type === 'live' ? 'Live Dashcam Video' : 'Historical Dashcam Video'}
                    </h2>
                    <p className="text-sm text-gray-600">
                      Vehicle: {videoModalData.vehicle.plate} ({videoModalData.vehicle.vehicle_type})
                      {videoModalData.startTime && videoModalData.endTime && (
                        <span className="ml-2">
                          • {new Date(videoModalData.startTime).toLocaleDateString()} to {new Date(videoModalData.endTime).toLocaleDateString()}
                        </span>
                      )}
                    </p>
                  </div>

                  {/* Channel Switcher - For both live and historical video */}
                  {dashcamMappings[videoModalData.vehicle.plate] && (
                    <div className="flex items-center gap-2 ml-auto mr-4">
                      <span className="text-sm font-medium text-gray-700">Channel:</span>
                      <div className="flex gap-1">
                        {Array.from({ length: dashcamMappings[videoModalData.vehicle.plate].channel_count }, (_, i) => i + 1).map((channelNum) => (
                          <button
                            key={channelNum}
                            onClick={() => switchChannel(channelNum)}
                            disabled={(videoModalData.type === 'live' ? isLoadingLiveVideo : isLoadingHistoricalVideo) || videoModalData.currentChannel === channelNum}
                            className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                              videoModalData.currentChannel === channelNum
                                ? 'bg-fleet-secondary text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            {channelNum}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={closeVideoModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Video Content */}
            <div className="p-4">
              <div className="aspect-video bg-black rounded-lg overflow-hidden">
                {videoModalData.url ? (
                  <iframe
                    src={videoModalData.url}
                    className="w-full h-full"
                    frameBorder="0"
                    allowFullScreen
                    title={`${videoModalData.type} video for ${videoModalData.vehicle.plate}`}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-white">
                    <div className="text-center">
                      <svg className="animate-spin w-8 h-8 mx-auto mb-2" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <p>Loading video...</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Video Controls Info */}
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Video Information</h4>
                <div className="grid grid-cols-2 gap-4 text-xs text-gray-600">
                  <div>
                    <span className="font-medium">Device Name:</span> {dashcamMappings[videoModalData.vehicle.plate]?.device_name || 'N/A'}
                  </div>
                  <div>
                    <span className="font-medium">Device ID:</span> {dashcamMappings[videoModalData.vehicle.plate]?.mettax_device_id}
                  </div>
                  <div>
                    <span className="font-medium">Channel:</span> {videoModalData.currentChannel || dashcamMappings[videoModalData.vehicle.plate]?.default_channel || 1}
                  </div>
                  <div>
                    <span className="font-medium">Type:</span> {videoModalData.type === 'live' ? 'Live Stream' : 'Historical Playback'}
                  </div>
                  <div>
                    <span className="font-medium">Quality:</span> Sub-stream (Optimized)
                  </div>
                  <div>
                    <span className="font-medium">Vehicle:</span> {videoModalData.vehicle.plate}
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-2 p-4 border-t">
              <button
                onClick={closeVideoModal}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
              {videoModalData.type === 'historical' && (
                <button
                  onClick={() => {
                    const startTime = prompt('Enter start time (YYYY-MM-DD HH:MM:SS):');
                    const endTime = prompt('Enter end time (YYYY-MM-DD HH:MM:SS):');
                    if (startTime && endTime) {
                      closeVideoModal();
                      setTimeout(() => openHistoricalVideo(videoModalData.vehicle, startTime, endTime), 100);
                    }
                  }}
                  className="px-4 py-2 text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Change Time Range
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Command Modal */}
      {showCommandModal && commandVehicle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Vehicle Commands</h2>
                <p className="text-sm text-gray-600">
                  {commandVehicle.plate} - {commandVehicle.name}
                </p>
              </div>
              <button
                onClick={() => setShowCommandModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Command Options */}
            <div className="p-4 space-y-3">
              <button
                onClick={() => sendCommand('wakeup')}
                disabled={isLoadingCommand}
                className="w-full flex items-center gap-3 p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <div className="font-medium text-gray-900">Request GPS Update</div>
                  <div className="text-sm text-gray-600">Wake up device and get fresh location</div>
                </div>
              </button>

              <button
                onClick={() => {
                  const odometer = prompt('Enter current odometer reading (km):');
                  if (odometer && !isNaN(parseFloat(odometer))) {
                    sendCommand('update_odometer', { odometer: parseFloat(odometer) });
                  }
                }}
                disabled={isLoadingCommand}
                className="w-full flex items-center gap-3 p-4 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <div className="font-medium text-gray-900">Update Odometer</div>
                  <div className="text-sm text-gray-600">Set current mileage reading</div>
                </div>
              </button>

              <button
                onClick={() => {
                  if (confirm(`Are you sure you want to LOCK ${commandVehicle.plate}?`)) {
                    sendCommand('lock');
                  }
                }}
                disabled={isLoadingCommand}
                className="w-full flex items-center gap-3 p-4 bg-green-50 hover:bg-green-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <div className="font-medium text-gray-900">Lock Vehicle</div>
                  <div className="text-sm text-gray-600">Remotely lock the doors</div>
                </div>
              </button>

              <button
                onClick={() => {
                  if (confirm(`Are you sure you want to UNLOCK ${commandVehicle.plate}?`)) {
                    sendCommand('unlock');
                  }
                }}
                disabled={isLoadingCommand}
                className="w-full flex items-center gap-3 p-4 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-amber-600 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <div className="font-medium text-gray-900">Unlock Vehicle</div>
                  <div className="text-sm text-gray-600">Remotely unlock the doors</div>
                </div>
              </button>

              <button
                onClick={() => {
                  if (confirm(`⚠️ WARNING: Are you ABSOLUTELY SURE you want to disable the engine on ${commandVehicle.plate}?\n\nThis will shut down the engine remotely and should only be used in emergency situations (e.g., stolen vehicle).`)) {
                    sendCommand('engine_off');
                  }
                }}
                disabled={isLoadingCommand}
                className="w-full flex items-center gap-3 p-4 bg-red-50 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed border-2 border-red-200"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <div className="font-medium text-red-900">Engine Off (Emergency)</div>
                  <div className="text-sm text-red-600">⚠️ Remotely disable engine - Use with caution!</div>
                </div>
              </button>

              {isLoadingCommand && (
                <div className="flex items-center justify-center py-4">
                  <svg className="animate-spin w-6 h-6 text-orange-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="ml-2 text-gray-600">Sending command...</span>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-2 p-4 border-t">
              <button
                onClick={() => setShowCommandModal(false)}
                disabled={isLoadingCommand}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Safety Dashboard Modal */}
      {showSafetyModal && (
        <SafetyDashboard
          fromDate={new Date(Date.now() - 7*24*60*60*1000).toISOString().split('T')[0]}
          toDate={new Date().toISOString().split('T')[0]}
          onClose={() => setShowSafetyModal(false)}
        />
      )}

      {/* Analytics Dashboard Modal */}
      {showAnalyticsModal && (
        <AnalyticsDashboard
          fromDate={new Date(Date.now() - 7*24*60*60*1000).toISOString().split('T')[0]}
          toDate={new Date().toISOString().split('T')[0]}
          onClose={() => setShowAnalyticsModal(false)}
        />
      )}

      {/* Geofence Monitor Modal */}
      {showGeofenceModal && (
        <GeofenceMonitor onClose={() => setShowGeofenceModal(false)} />
      )}

      {/* Events Timeline Modal */}
      {showEventsModal && (
        <EventsTimeline onClose={() => setShowEventsModal(false)} />
      )}

      {/* Vehicle Search Modal */}
      {showVehicleSearch && (
        <VehicleSearch 
          onClose={() => setShowVehicleSearch(false)}
          onSelectVehicle={(vehicle) => {
            // Find the matching vehicle in our vehicles array
            const matchedVehicle = vehicles.find(v => v.plate === vehicle.license_plate);
            if (matchedVehicle) {
              setSelectedVehicle(matchedVehicle);
              setShowVehicleSearch(false);
            }
          }}
        />
      )}
    </div>
  );
};

export default IturanLiveMap;
